import { watch, onBeforeUnmount, nextTick, toValue } from 'vue'

const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ')

function isVisible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)
}

export function getFocusableEls(root) {
    if (!root) return []
    return Array.from(root.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.tabIndex !== -1 && isVisible(el)
    )
}

// Shared across all trap instances so nested dialogs (e.g. a confirm Modal
// opened from within a SlideIn panel) don't unlock scroll for each other
// while either one is still open.
let scrollLockCount = 0

function lockScroll() {
    if (scrollLockCount === 0) {
        document.body.style.overflow = 'hidden'
    }
    scrollLockCount++
}

function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1)
    if (scrollLockCount === 0) {
        document.body.style.overflow = ''
    }
}

/**
 * Moves focus into `containerRef` while `isOpen` is true, restores it to
 * whatever was focused beforehand once it closes, traps Tab within the
 * container's focusable elements, and locks background scroll for as long
 * as any trap is active. `containerRef` should point at a `tabindex="-1"`
 * dialog root so it's focusable even with no other focusable descendants.
 */
export function useFocusTrap(containerRef, isOpen) {
    let previouslyFocused = null
    let isActive = false

    function handleKeydown(event) {
        if (event.key !== 'Tab') return
        const root = containerRef.value
        const els = getFocusableEls(root)
        if (!els.length) {
            // Nothing to wrap between — keep focus pinned on the root
            // rather than letting Tab escape to background content.
            if (document.activeElement === root) {
                event.preventDefault()
            }
            return
        }
        const first = els[0]
        const last = els[els.length - 1]
        if (event.shiftKey) {
            if (
                document.activeElement === first ||
                document.activeElement === root
            ) {
                event.preventDefault()
                last.focus()
            }
        } else if (document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }

    function deactivate() {
        if (!isActive) return
        isActive = false
        document.removeEventListener('keydown', handleKeydown)
        unlockScroll()
        // The trigger may have been removed from the DOM while the dialog
        // was open (e.g. deleting the row that opened it) — only refocus
        // it if it's still there.
        if (previouslyFocused && document.body.contains(previouslyFocused)) {
            previouslyFocused.focus()
        }
        previouslyFocused = null
    }

    watch(
        () => toValue(isOpen),
        async (open) => {
            if (open) {
                isActive = true
                previouslyFocused = document.activeElement
                document.addEventListener('keydown', handleKeydown)
                lockScroll()
                await nextTick()
                containerRef.value?.focus()
            } else {
                deactivate()
            }
        },
        { immediate: true }
    )

    onBeforeUnmount(deactivate)
}
